/// Port of mobile/lib/auth/data/api_auth_repository.dart.
/// Live auth against the Go API (/v1/auth/*, /v1/users/me).

import { ApiError, apiClient } from '@/app/network/apiClient';
import { clearSessionToken, readSessionToken, writeSessionToken } from '@/app/network/sessionTokenStore';
import { emailStatusFromJson, type EmailStatus } from '@/auth/domain/emailStatus';
import { readOrCreateAnonymousId } from '@/user/data/userLocalData';
import type { User } from '@/user/domain/user';
import { userFromJson } from '@/user/domain/user';

interface SessionResponse {
  token?: string;
  user?: Record<string, unknown>;
  needs_username?: boolean;
}

export interface SignInResult {
  user: User;
  needsUsername: boolean;
}

function persistSession(body: SessionResponse): SignInResult {
  if (!body.token || !body.user) {
    throw new ApiError('Malformed sign-in response', 0);
  }
  writeSessionToken(body.token);
  return {
    user: userFromJson(body.user),
    needsUsername: Boolean(body.needs_username),
  };
}

export async function restoreSession(): Promise<User | null> {
  if (readSessionToken() === null) return null;

  try {
    const body = await apiClient.get<Record<string, unknown>>('/v1/users/me');
    return userFromJson(body);
  } catch (error) {
    if (error instanceof ApiError && error.isUnauthorized) {
      clearSessionToken();
      return null;
    }
    throw error;
  }
}

export async function signIn(email: string, password: string): Promise<User> {
  const body = await apiClient.post<SessionResponse>('/v1/auth/login', {
    email,
    password,
    anonymous_id: readOrCreateAnonymousId(),
  });
  return persistSession(body).user;
}

export async function signUp(
  email: string,
  password: string,
  proofToken: string,
  username?: string,
): Promise<User> {
  const payload: Record<string, string> = {
    email,
    password,
    proof_token: proofToken,
    anonymous_id: readOrCreateAnonymousId(),
  };
  const trimmed = username?.trim();
  if (trimmed) payload.username = trimmed;

  const body = await apiClient.post<SessionResponse>('/v1/auth/register', payload);
  return persistSession(body).user;
}

export async function sendOtp(email: string, purpose = 'register'): Promise<{ resendAfterSeconds: number }> {
  return apiClient.post<{ resend_after_seconds: number }>(
    '/v1/auth/send-otp',
    { email, purpose },
    { anonymous: true },
  ).then((body) => ({ resendAfterSeconds: body.resend_after_seconds ?? 60 }));
}

export async function verifyOtp(
  email: string,
  code: string,
  purpose = 'register',
): Promise<string> {
  const body = await apiClient.post<{ proof_token?: string }>(
    '/v1/auth/verify-otp',
    { email, code, purpose },
    { anonymous: true },
  );
  if (!body.proof_token) throw new ApiError('Malformed verify response', 0);
  return body.proof_token;
}

export async function verifyOtpForUser(code: string): Promise<User> {
  const body = await apiClient.post<Record<string, unknown>>('/v1/auth/verify-otp-user', { code });
  return userFromJson(body);
}

export async function emailStatus(email: string): Promise<EmailStatus> {
  const body = await apiClient.post<Record<string, unknown>>(
    '/v1/auth/email-status',
    { email },
    { anonymous: true },
  );
  return emailStatusFromJson(body);
}

export async function signInWithGoogle(idToken: string): Promise<SignInResult> {
  const body = await apiClient.post<SessionResponse>('/v1/auth/google', {
    identity_token: idToken,
    anonymous_id: readOrCreateAnonymousId(),
  });
  return persistSession(body);
}

export async function setPassword(password: string): Promise<User> {
  const body = await apiClient.post<Record<string, unknown>>('/v1/auth/password', { password });
  return userFromJson(body);
}

export async function signOut(): Promise<void> {
  try {
    await apiClient.post<void>('/v1/auth/logout');
  } catch (error) {
    // A dead session is still a successful local sign-out.
    if (!(error instanceof ApiError && error.isUnauthorized)) throw error;
  } finally {
    clearSessionToken();
  }
}

export async function verifyEmail(token: string): Promise<User> {
  const body = await apiClient.post<Record<string, unknown>>(
    '/v1/auth/verify-email',
    { token },
    { anonymous: true },
  );
  return userFromJson(body);
}

export async function resendVerification(): Promise<void> {
  await apiClient.post<void>('/v1/auth/resend-verification');
}

export async function requestChangeEmail(email: string): Promise<number> {
  const body = await apiClient.post<{ resend_after_seconds?: number }>(
    '/v1/auth/change-email/send-otp',
    { email },
  );
  return body.resend_after_seconds ?? 60;
}

export async function confirmChangeEmail(email: string, code: string): Promise<User> {
  const body = await apiClient.post<Record<string, unknown>>('/v1/auth/change-email', {
    email,
    code,
  });
  return userFromJson(body);
}

export async function forgotPassword(email: string): Promise<void> {
  await apiClient.post<void>('/v1/auth/forgot-password', { email }, { anonymous: true });
}

export async function resetPassword(token: string, password: string): Promise<void> {
  await apiClient.post<void>('/v1/auth/reset-password', { token, password }, { anonymous: true });
}
